'use strict';

/**
 * NACRE Wave 2.4.3 — recovery-client
 *
 * Browser-side mnemonic-based recovery. Two functions:
 *
 *   unlockWithMnemonic — unwraps the identity vault using the BIP39
 *     mnemonic-derived key instead of the password-derived key. Used to
 *     read your vault when you've forgotten your password.
 *
 *   buildPasswordReset — does the unlock, then derives a NEW key from a
 *     new password and re-wraps the existing identityMaster. Returns the
 *     wire payload the server needs to atomically replace the meta. Does
 *     NOT change the underlying identityMaster — so all wrapped module
 *     masters and private keys keep working unchanged.
 *
 * Pure functions, no I/O. Browser-safe (only @noble/* and our own primitives).
 */

const { hkdfSubkey } = require('./kdf');
const symmetric = require('./symmetric');
const { mnemonicToSeed, validateMnemonic } = require('./seed');
const { deriveAuthProof } = require('./auth-proof');
// T901 D5 step 3 — ONE re-wrap implementation, two gates. `buildPasswordReset`
// (mnemonic gate) and `password-rewrap.buildPasswordChange` (current-password
// gate) perform the IDENTICAL operation once the vault is open; writing the
// tail twice is the "unused twin" shape this repo keeps getting bitten by.
const { rewrapIdentityMasterUnderPassword } = require('./password-rewrap');
const {
  recoveryFingerprint,
  legacyRecoveryFingerprint,
} = require('./recovery-fingerprint');

const SEED_LABEL_IDENTITY = 'oyster:vault:identity:v1';

function deriveKeySeed(mnemonic, saltSeed) {
  if (!validateMnemonic(mnemonic)) {
    throw new Error('Invalid mnemonic for recovery');
  }
  const seedBytes = mnemonicToSeed(mnemonic);
  const combined = new Uint8Array(seedBytes.length + saltSeed.length);
  combined.set(seedBytes, 0);
  combined.set(saltSeed, seedBytes.length);
  return hkdfSubkey(combined, SEED_LABEL_IDENTITY, 32);
}

function deserializeFromStorage(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (typeof value.__u8 === 'string') {
      return new Uint8Array(Buffer.from(value.__u8, 'hex'));
    }
    const out = {};
    for (const k of Object.keys(value)) out[k] = deserializeFromStorage(value[k]);
    return out;
  }
  if (Array.isArray(value)) return value.map(deserializeFromStorage);
  return value;
}

function validateMetaForRecovery(meta) {
  if (!meta || typeof meta !== 'object') {
    throw new Error('Invalid meta: must be an object');
  }
  // T210 W3: recoveryFingerprint is NO LONGER required in the meta the client
  // receives — the server withholds it over the unauthenticated /recovery/begin,
  // and the client derives the proof itself from the mnemonic. The authoritative
  // gate is the wrapSeed auth-tag check, not the (optional) fingerprint pre-check.
  const required = ['saltSeed', 'wrapSeed', 'publicKeys'];
  for (const f of required) {
    if (meta[f] === undefined || meta[f] === null) {
      throw new Error('Invalid meta: missing required field "' + f + '"');
    }
  }
}

/**
 * Unwrap an identity vault using the user's BIP39 mnemonic.
 *
 * @returns {{publicKeys, privateKeys, moduleMasters, policies, _identityMaster}}
 */
function unlockWithMnemonic({ mnemonic, meta, vaultBin }) {
  validateMetaForRecovery(meta);
  // Verify fingerprint BEFORE attempting unwrap → clean error message.
  // Derive the proof the server will compare against the stored fingerprint.
  // Union (T210 W3): a new account stored the stretched, per-account-salted
  // fingerprint; a legacy account stored the bare sha256. The server withholds
  // the stored value over the unauthenticated /recovery/begin (T210 W3-6), so
  // the client can only fast-fail when the fingerprint is present; the
  // authoritative gate is always the wrapSeed auth-tag check below.
  // The fingerprint KDF profile is PINNED (deriver default = DESKTOP) so signup
  // and recovery derive identically regardless of device — independent of the
  // device-detected password kdfProfile.
  const stretchedFp = recoveryFingerprint(mnemonic, { saltSeed: meta.saltSeed });
  const legacyFp = legacyRecoveryFingerprint(mnemonic);
  let matchedFingerprint = stretchedFp; // default to the new form
  if (meta.recoveryFingerprint !== undefined && meta.recoveryFingerprint !== null) {
    if (stretchedFp === meta.recoveryFingerprint) {
      matchedFingerprint = stretchedFp;
    } else if (legacyFp === meta.recoveryFingerprint) {
      matchedFingerprint = legacyFp; // legacy account — send the bare form as proof
    } else {
      throw new Error('Recovery mnemonic does not match this account');
    }
  }
  const saltSeed = Buffer.from(meta.saltSeed, 'hex');
  const keySeed = deriveKeySeed(mnemonic, saltSeed);

  let identityMaster;
  try {
    identityMaster = symmetric.decrypt(meta.wrapSeed, keySeed, { binary: true });
  } catch (err) {
    throw new Error('Recovery unwrap failed (auth tag verification)');
  }

  let innerJson;
  try {
    innerJson = symmetric.decrypt(vaultBin, identityMaster);
  } catch (err) {
    throw new Error('Vault binary decryption failed during recovery');
  }
  const inner = deserializeFromStorage(JSON.parse(innerJson));

  return {
    publicKeys: deserializeFromStorage(meta.publicKeys),
    privateKeys: inner.privateKeys,
    moduleMasters: inner.moduleMasters || {},
    policies: inner.policies || {},
    _identityMaster: identityMaster,
    // The fingerprint proof the server must compare against the stored value —
    // derived from the mnemonic, NOT echoed from a (now-withheld) meta field.
    _recoveryFingerprintProof: matchedFingerprint,
  };
}

/**
 * Build the wire payload for a password reset. The browser:
 *   1. Unwraps the existing identityMaster using the mnemonic
 *   2. Derives a new keyPw from the new password with a fresh saltPw
 *   3. Re-wraps identityMaster under the new keyPw
 *   4. Returns the (newSaltPw, newWrapPw) for the server to apply
 *
 * The new wrap is functionally equivalent to the old one — same
 * identityMaster, just sealed under a different password. All downstream
 * vaults (module masters, private keys) work unchanged.
 *
 * @returns {{
 *   newSaltPw: string,
 *   newWrapPw: object,
 *   recoveryFingerprintProof: string
 * }}
 */
function buildPasswordReset({ mnemonic, newPassword, meta, vaultBin, kdfProfile }) {
  if (typeof newPassword !== 'string' || newPassword.length === 0) {
    throw new Error('Invalid newPassword: must be a non-empty string');
  }
  // Unwrap with the mnemonic to retrieve the existing identityMaster
  const opened = unlockWithMnemonic({ mnemonic, meta, vaultBin });

  // Fresh salt + re-wrap + the T207 client-derived verifier, through the ONE
  // shared implementation (`shared/crypto/password-rewrap.js`). This body used
  // to inline those three steps; it was extracted verbatim so the
  // current-password variant cannot drift from the mnemonic one.
  const { newSaltPw, newWrapPw, newPasswordProof } = rewrapIdentityMasterUnderPassword({
    identityMaster: opened._identityMaster,
    newPassword,
    kdfProfile,
  });

  return {
    newSaltPw,
    newWrapPw,
    newPasswordProof,
    // T210 W3: forward the fingerprint DERIVED from the mnemonic (unlock picked
    // stretched-vs-legacy to match what's stored). Do NOT echo a meta field —
    // the server withholds meta.recoveryFingerprint over /recovery/begin.
    recoveryFingerprintProof: opened._recoveryFingerprintProof,
  };
}

module.exports = {
  unlockWithMnemonic,
  buildPasswordReset,
  deriveAuthProof,
  // Re-export the shared stretched fingerprint deriver (T210 W3).
  recoveryFingerprint,
  legacyRecoveryFingerprint,
};
